import React, { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'

import { Dialog } from '../../../../uikit/Dialog'
import { Dropdown } from '../../../../uikit/Dropdown'
import { usePartnerGetCompaniesByPartnerIdQuery } from '../../../../redux/api/web-api'
import useText from '../../../../hooks/useText'

const formId = 'add-rfid-form'

export type AddRFIDDialogProps = {
    visible: boolean
    onHide: () => void
    onSubmit: (organisationId: string) => void
    partnerId: number
}

export const AddRFIDDialog: FC<AddRFIDDialogProps> = ({ visible, onHide, onSubmit, partnerId }) => {
    const text = useText()
    const { data: organisations = [], isLoading } = usePartnerGetCompaniesByPartnerIdQuery(
        { partnerId },
        { skip: !visible }
    )

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isSubmitted },
    } = useForm<{ organisationId: string }>({
        defaultValues: {
            organisationId: '',
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        if (visible) {
            reset()
        }
    }, [reset, visible])

    const organisationOptions = organisations.map((organisation) => ({
        value: organisation?.id,
        label: organisation?.name || '',
    }))
    const saveDisabled = isSubmitted && !!errors.organisationId
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} data-testid="btn-cancel" />
            <Button
                label={text('btnSave')}
                type="submit"
                form={formId}
                disabled={saveDisabled}
                loading={isLoading}
                data-testid="btn-save"
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblAddRFIDTagsModal')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="add-rfid-dialog"
        >
            <form
                id={formId}
                className="flex flex-column gap-2"
                noValidate
                onSubmit={handleSubmit(({ organisationId }) => onSubmit(organisationId))}
            >
                <Controller
                    name="organisationId"
                    control={control}
                    rules={{ required: text('msgOrganisationRequired') }}
                    render={({ field }) => (
                        <Dropdown
                            id="select-organisation"
                            data-testid="select-organisation"
                            label={text('lblOrganisationField')}
                            value={field.value ?? null}
                            options={organisationOptions}
                            onChange={(event) => field.onChange(event.value)}
                            onBlur={field.onBlur}
                            error={errors.organisationId?.message}
                            required
                        />
                    )}
                />
            </form>
        </Dialog>
    )
}
